import React, { memo, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trackEvent } from "../utils/analytics/track";
import { RECORDING_MODES } from "../utils/recordingModes";
import { getReviewMetadataPath } from "../utils/navigation/recordingModeNav";
import GuidanceAccordion from "../components/guidance/GuidanceAccordion";
import GuidanceBulletList from "../components/guidance/GuidanceBulletList";

function CardiopulmonaryGuidancePage() {
  const { t } = useTranslation();

  const recordAgainPath = useMemo(
    () =>
      getReviewMetadataPath(
        "/recording/tidal-breathing/camera",
        RECORDING_MODES.TIDAL,
      ),
    [],
  );

  return (
    <div className="w-full max-w-lg h-full min-h-0 overflow-y-auto p-4">
      <section className="min-h-full flex flex-col gap-2">
        <header className="flex flex-col items-start text-start gap-1">
          <h1 className="subtitle-text">
            {t(
              "guidance.cardiopulmonary.title",
              "Improve your cardiopulmonary recording",
            )}
          </h1>
          <p>
            {t(
              "guidance.cardiopulmonary.intro",
              "Use these tips before repeating the recording.",
            )}
          </p>
        </header>

        <GuidanceAccordion
          title={t(
            "guidance.cardiopulmonary.why.title",
            "Why a result may fail",
          )}
          defaultOpen
        >
          <GuidanceBulletList
            items={[
              t(
                "guidance.cardiopulmonary.why.cardiac",
                "Cardiac quality can fail if the camera cannot clearly detect small colour changes used to estimate heart rate.",
              ),
              t(
                "guidance.cardiopulmonary.why.respiratory",
                "Respiratory quality can fail if chest and abdomen movement is hard to see or if there is too much movement.",
              ),
              t(
                "guidance.cardiopulmonary.why.overall",
                "Overall recording quality can fail because of poor lighting, camera shake, interrupted recording, or moving away from the frame.",
              ),
            ]}
          />
        </GuidanceAccordion>

        <GuidanceAccordion
          title={t(
            "guidance.cardiopulmonary.recording.title",
            "Recording and camera tips",
          )}
        >
          <GuidanceBulletList
            items={[
              t(
                "guidance.cardiopulmonary.recording.stable",
                "Place the phone on a stable surface and keep it hands-free.",
              ),
              t(
                "guidance.cardiopulmonary.recording.frame",
                "Sit far enough back so your face, shoulders, chest, and upper abdomen remain visible.",
              ),
              t(
                "guidance.cardiopulmonary.recording.hands",
                "Place one hand on your chest and one hand on your upper abdomen when instructed, keeping both hands visible.",
              ),
              t(
                "guidance.cardiopulmonary.recording.light",
                "Use steady lighting and avoid shadows, glare, or a bright window behind you.",
              ),
            ]}
          />
        </GuidanceAccordion>

        <GuidanceAccordion
          title={t(
            "guidance.cardiopulmonary.during.title",
            "During the recording",
          )}
        >
          <GuidanceBulletList
            items={[
              t(
                "guidance.cardiopulmonary.during.still",
                "Stay as still as possible until the recording is complete.",
              ),
              t(
                "guidance.cardiopulmonary.during.breathe",
                "Breathe normally and avoid talking, laughing, or changing posture.",
              ),
              t(
                "guidance.cardiopulmonary.during.prompt",
                "Follow the on-screen prompts and repeat the recording if the app asks you to.",
              ),
            ]}
          />
        </GuidanceAccordion>

        <div className="mt-auto w-full grid grid-cols-1 gap-2 pb-2">
          <Link
            to={recordAgainPath}
            className="primary-button w-full"
            onClick={() =>
              trackEvent("cardiopulmonary_guidance_click_record_again", {
                mode: RECORDING_MODES.TIDAL,
              })
            }
          >
            {t(
              "guidance.cardiopulmonary.recordAgain",
              "Record cardiopulmonary again",
            )}
          </Link>
        </div>
      </section>
    </div>
  );
}

export default memo(CardiopulmonaryGuidancePage);
